import DOMPurify from "dompurify";
import { sanitizeMailHtml } from "./sanitizeMailHtml";
import type { MailMessage } from "./types";

const MAX_QUOTE_FONT_PX = 18;
/** Keep reply payloads under Edge Function / SMTP practical limits. */
const MAX_QUOTE_HTML_CHARS = 80_000;

/** Inline styles that survive Gmail / Apple Mail / Outlook (like gmail_quote). */
export const MAIL_QUOTE_BLOCKQUOTE_STYLE =
  "margin:0 0 0 0.8ex;border-left:2px solid #3474E0;padding-left:1ex";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Apple Mail–style attribution date: "Sep 16, 2026, at 4:29 PM". */
export function formatReplyQuoteWhen(sentAt: string | Date): string {
  const date = typeof sentAt === "string" ? new Date(sentAt) : sentAt;
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart}, at ${timePart}`;
}

/** "Justin Babiy <jbabiy@vendasta.com>" or email alone. */
export function formatReplyQuoteWho(message: MailMessage): string {
  const name = message.from_name?.trim();
  const email = message.from_email?.trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${escapeHtml(name)} &lt;${escapeHtml(email)}&gt;`;
  }
  return escapeHtml(email || name || "Unknown");
}

function messageQuoteSource(
  last: MailMessage | undefined,
  snippet: string | null | undefined,
): string {
  if (last?.body_html?.trim()) return last.body_html;
  if (last?.body_text?.trim()) {
    return `<p>${escapeHtml(last.body_text).replace(/\n/g, "<br/>")}</p>`;
  }
  if (snippet?.trim()) {
    return `<p>${escapeHtml(snippet)}</p>`;
  }
  return "";
}

function flattenQuoteLayout(html: string): string {
  if (typeof document === "undefined") return html;
  const root = document.createElement("div");
  root.innerHTML = html;

  root.querySelectorAll("style, link, meta, base").forEach((el) => el.remove());

  root.querySelectorAll("*").forEach((node) => {
    const el = node as HTMLElement;
    const pos = el.style.position?.toLowerCase();
    if (pos === "fixed" || pos === "absolute" || pos === "sticky") {
      el.style.position = "static";
    }
    el.style.maxWidth = "100%";
    el.style.boxSizing = "border-box";
    if (el.tagName === "TABLE") {
      el.removeAttribute("width");
      el.style.width = "100%";
    }
    if (el.tagName === "IMG") {
      el.style.maxWidth = "100%";
      el.style.height = "auto";
      el.style.objectFit = "contain";
      const widthAttr = Number.parseInt(el.getAttribute("width") ?? "", 10);
      if (!Number.isFinite(widthAttr) || widthAttr <= 0) {
        el.style.maxHeight = "200px";
      }
    }
  });

  root.querySelectorAll("[style]").forEach((node) => {
    const el = node as HTMLElement;
    let style = el.getAttribute("style") ?? "";
    style = style.replace(
      /font-size\s*:\s*([\d.]+)\s*(px|pt|em|rem)/gi,
      (match, num, unit) => {
        const value = Number(num);
        if (!Number.isFinite(value)) return match;
        let px = value;
        if (String(unit).toLowerCase() === "pt") px = value * 1.33;
        if (
          String(unit).toLowerCase() === "em" ||
          String(unit).toLowerCase() === "rem"
        ) {
          px = value * 16;
        }
        if (px > MAX_QUOTE_FONT_PX) return "font-size:14px";
        return match;
      },
    );
    // Keep small icon widths; only loosen large fixed widths that break the pane.
    style = style.replace(/width\s*:\s*([\d.]+)\s*px/gi, (match, num) => {
      const px = Number(num);
      if (Number.isFinite(px) && px > 200) {
        return "max-width:100%;width:auto";
      }
      return match;
    });
    style = style.replace(/min-width\s*:\s*[\d.]+\s*px/gi, "min-width:0");
    el.setAttribute("style", style);
  });

  return root.innerHTML;
}

/** Sanitized HTML safe to show in compose quote pane and to append when sending. */
export function prepareQuotedHtmlForCompose(raw: string): string {
  if (!raw.trim()) return "<p></p>";
  const stripped = raw.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
  const sanitized = sanitizeMailHtml(stripped);
  const noStyle = DOMPurify.sanitize(sanitized, {
    FORBID_TAGS: ["style", "link", "meta", "iframe", "object", "embed", "form"],
  });
  let prepared = flattenQuoteLayout(noStyle);
  if (prepared.length > MAX_QUOTE_HTML_CHARS) {
    // Drop remote images first — they dominate quote size and blow up reply.
    prepared = prepared.replace(/<img\b[^>]*>/gi, "");
  }
  if (prepared.length > MAX_QUOTE_HTML_CHARS) {
    // Cut on a tag boundary when possible to avoid mid-tag truncation.
    const slice = prepared.slice(0, MAX_QUOTE_HTML_CHARS);
    const lastGt = slice.lastIndexOf(">");
    prepared = `${lastGt > MAX_QUOTE_HTML_CHARS * 0.8 ? slice.slice(0, lastGt + 1) : slice}…`;
  }
  return prepared;
}

/**
 * Gmail/Apple-compatible reply quote for compose + outbound MIME.
 * Recipients see blue left bar + "On DATE, Name <email> wrote:".
 */
export function buildReplyQuoteBlock(
  last: MailMessage | undefined,
  snippet: string | null,
): string {
  const quoted = prepareQuotedHtmlForCompose(messageQuoteSource(last, snippet));
  const who = last ? formatReplyQuoteWho(last) : "Unknown";
  const when = last?.sent_at ? formatReplyQuoteWhen(last.sent_at) : "";
  const attr = when ? `On ${when}, ${who} wrote:` : `${who} wrote:`;
  return (
    `<div class="gmail_quote mail-compose-quote">` +
    `<div class="gmail_attr" style="color:#5f6368;margin:0 0 8px">${attr}<br></div>` +
    `<blockquote class="gmail_quote mail-compose-quote" style="${MAIL_QUOTE_BLOCKQUOTE_STYLE}">` +
    `${quoted || "<p></p>"}` +
    `</blockquote></div>`
  );
}

export function buildForwardQuoteBlock(
  last: MailMessage | undefined,
  snippet: string | null,
): string {
  const quoted = prepareQuotedHtmlForCompose(messageQuoteSource(last, snippet));
  const who = last ? formatReplyQuoteWho(last) : "";
  const when = last?.sent_at ? formatReplyQuoteWhen(last.sent_at) : "";
  const metaBits = [
    who ? `From: ${who}` : null,
    when ? `Date: ${when}` : null,
    last?.subject?.trim()
      ? `Subject: ${escapeHtml(last.subject.trim())}`
      : null,
  ].filter(Boolean);
  const meta =
    metaBits.length > 0
      ? `<div class="gmail_attr" style="color:#5f6368;margin:0 0 8px">${metaBits.join("<br>")}<br></div>`
      : "";
  return (
    `<div class="gmail_quote mail-compose-quote">` +
    `<div style="margin:0 0 8px">---------- Forwarded message ----------</div>` +
    meta +
    `<blockquote class="gmail_quote mail-compose-quote" style="${MAIL_QUOTE_BLOCKQUOTE_STYLE}">` +
    `${quoted || "<p></p>"}` +
    `</blockquote></div>`
  );
}

/** @deprecated use buildReplyQuoteBlock + separate user editor */
export function buildReplyQuoteHtml(
  last: MailMessage | undefined,
  snippet: string | null,
): string {
  return `<p><br></p>${buildReplyQuoteBlock(last, snippet)}`;
}

/** @deprecated use buildForwardQuoteBlock + separate user editor */
export function buildForwardQuoteHtml(
  last: MailMessage | undefined,
  snippet: string | null,
): string {
  return `<p><br></p>${buildForwardQuoteBlock(last, snippet)}`;
}

/** Top-posting: user reply first, then quoted original (Gmail/Apple). */
export function assembleComposeBody(
  userHtml: string,
  quotedHtml: string | null | undefined,
): string {
  const top = userHtml.trim() || "<p></p>";
  if (!quotedHtml?.trim()) return top;
  return `${top}<br>${quotedHtml}`;
}
